import { DismissableLayerBranch } from '@radix-ui/react-dismissable-layer';
import { Toaster } from 'sonner';
import { useTheme } from '@/components/theme-provider';

/**
 * The app's one toast viewport — mounted once, in `main.tsx`.
 *
 * WHERE: bottom-center, the one spot no action lives. Every page header puts
 * its primary actions top-right (Save, Approve, Send, the ⋯ menu), so a
 * top-right stack sat on them and swallowed clicks. Bottom-right is no better:
 * every form sheet's Save/Cancel footer is right-aligned at the bottom, and
 * most `toast.error` calls fire while that sheet is open. Bottom-center clears
 * both — even the widest sheet (max-w-2xl) keeps its right-aligned buttons
 * outside a centred toast — and the sidebar's user menu bottom-left. A hovered
 * stack expands upward into page content, never into the header.
 *
 * `closeButton` so a toast can be dismissed without waiting it out: sonner
 * pauses the timer while the stack is hovered or the tab is hidden.
 *
 * `theme` because sonner defaults to light and knows nothing of our
 * ThemeProvider — without it every toast rendered light in dark mode. The two
 * share the same 'light' | 'dark' | 'system' vocabulary, so it passes through.
 *
 * UNDER A MODAL: the toaster lives outside every Radix layer's React tree, which
 * broke clicking a toast while a Sheet/Dialog was open. The Branch wrapper and
 * its class fix both halves:
 *
 * 1. Clicking a toast is an *outside* interaction, so the open Sheet/Dialog
 *    dismisses — dropping, or prompting over, the user's unsaved input. A
 *    DismissableLayer Branch (the mechanism Radix's own Toast viewport uses)
 *    marks the toaster as part of every layer, for pointerdown and focus alike.
 * 2. A modal layer sets `pointer-events: none` on <body>, which toasts inherit
 *    (sonner never sets `auto`), so the click falls through to the overlay and
 *    the close/action buttons are dead. Visible toasts opt back in; hidden ones
 *    keep sonner's `pointer-events: none`, and the empty toaster area stays
 *    click-through.
 *
 * The Branch must come from the same `@radix-ui/react-dismissable-layer` copy
 * `radix-ui` uses (its context is module-level), so the dependency is pinned
 * to the version `radix-ui` pins — bump them together.
 */
export function AppToaster() {
  const { theme } = useTheme();
  return (
    <DismissableLayerBranch className="[&_[data-sonner-toast]:not([data-visible=false])]:pointer-events-auto">
      <Toaster closeButton position="bottom-center" richColors theme={theme} />
    </DismissableLayerBranch>
  );
}
